import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { ApiError } from '../enums/api-error.enum';
import { PopupWindowID } from '../enums/popup-window-id.enum';
import { IQuizResult } from '../interfaces/quiz-result.interface';
import { IQuizError } from '../interfaces/quiz-error.interface';
import { IQuestionHeaderRect, IQuizState } from '../interfaces/quiz-state.interface';
import { IAnswer } from '../interfaces/answer.interface';
import { ILead } from '../interfaces/lead.interface';

const quizState: IQuizState = {
  wasOpened: false,
  wasStarted: false,
  wasPassed: false,
  wasClosed: false,
  hasContent: false,
  appointmentDateSent: false,
  popupWidth: 0,
  progress: 0,
  errors: [],
  results: [],
  receivedLead: undefined,
  questionHeaderRect: undefined,
}

export const quizSlice = createSlice({
  name: "quiz",
  initialState: quizState,
  reducers: {
    setQuizWasOpened: (state) => {
      state.wasOpened = true;
    },
  
    setQuizWasStarted: (state) => {
      state.wasStarted = true;
    },
  
    setQuizWasPassed: (state) => {
      state.wasPassed = true;
    },
  
    setQuizWasClosed: (state) => {
      state.wasClosed = true;
    },
  
    setQuizHasContent: (state) => {
      state.hasContent = true;
    },
  
    setProgress: (state, action: PayloadAction<number>) => {
      state.progress = action.payload;
    },
  
    pushQuizResult: (state, action: PayloadAction<IQuizResult>) => {
      const currentResultIndex = state.results.findIndex(result => result.questionId == action.payload.questionId);
      if (currentResultIndex != -1) {
        state.results[currentResultIndex] = action.payload;
        state.results.length = currentResultIndex + 1;
      } else {
        state.results.push(action.payload);
      }
    },

    resetQuizContentResults: (state) => {
      const distributorQuestionIndex = state.results.findIndex(result => result.distributor);
      const quizResults = state.results.filter((_, index) => index <= distributorQuestionIndex);
      state.results = quizResults ? quizResults : [];
    },

    rollupResults: (state, action: PayloadAction<number>) => {
      const resultIndex = state.results.findIndex(result => result.questionId == action.payload);
      if (resultIndex != -1) state.results.length = resultIndex;
    },
  
    pushError: (state, action: PayloadAction<IQuizError>) => {
      state.errors.push(action.payload);
    },
  
    resetErrors: (state) => {
      state.errors = [];
    },
  
    setApiError: (state, action: PayloadAction<ApiError>) => {
      state.apiError = action.payload;
    },
  
    setActivePopupWidnowId: (state, action: PayloadAction<PopupWindowID>) => {
      state.activePopupWindowID = action.payload;
    },
  
    setPopupWidth: (state, action: PayloadAction<number>) => {
      state.popupWidth = action.payload;
    },
  
    setDistributorSelectedAnswer: (state, action: PayloadAction<IAnswer>) => {
      state.distributorSelectedAnswer = action.payload;
    },

    setAppointmentDateSent: (state) => {
      state.appointmentDateSent = true;
    },

    receiveLead: (state, action: PayloadAction<ILead>) => {
      state.receivedLead = action.payload;
    },

    setQuestionHeaderRect: (state, action: PayloadAction<IQuestionHeaderRect>) => {
      state.questionHeaderRect = action.payload;
    },

    setExpertsCommentHeight: (state, action: PayloadAction<number>) => {
      state.expertsCommentHeight = action.payload;
    }
  }
});

export const { 
  setQuizWasOpened, 
  setQuizWasClosed, 
  setQuizWasPassed, 
  setQuizWasStarted, 
  setQuizHasContent, 
  setApiError, 
  setActivePopupWidnowId, 
  setDistributorSelectedAnswer, 
  setPopupWidth, 
  setProgress,
  pushError,
  pushQuizResult,
  resetErrors,
  resetQuizContentResults,
  rollupResults,
  setAppointmentDateSent,
  receiveLead,
  setQuestionHeaderRect,
  setExpertsCommentHeight
} = quizSlice.actions;

export default quizSlice.reducer;